/**
 * ========================================
 * CUSTOM ASSET - accordion-modded.js
 * Version: 1.1
 * Created: 2026-10-01
 * Last Modified: 2026-10-21
 * Client: Studio Optix
 * ========================================
 *
 * PURPOSE:
 * <accordion-group-modded> custom element wrapping the rows of an
 * "Accordion modded" block (blocks/accordion-modded.liquid). Owns the
 * group-level behaviors that Liquid can't express (child blocks can't
 * read parent block settings):
 *
 * - [data-exclusive]: stamps a shared `name` on every row's <details> so
 *   the browser natively closes siblings when a row opens. Before stamping,
 *   enforces "first wins" when several rows are marked open-by-default
 *   (with names already set, the browser would let the LAST one win).
 * - [data-scroll-on-open]: after a row opens, scrolls its header back into
 *   view if the closing of a taller row above pushed it off-screen.
 * - Anchor deep-links: on load and on hashchange, a #hash matching a row's
 *   Anchor ID opens that row and scrolls to it (rows carry
 *   scroll-margin-block-start for the sticky header).
 *
 * MODIFICATIONS:
 * - v1.1 (2026-10-21): Scope to OWN rows only — accordions can now nest
 *   (booking triage holds the patient fork). Previously
 *   querySelectorAll('details') also grabbed a nested accordion's rows,
 *   so the outer exclusive group stamped its name on them and opening
 *   an inner row slammed the outer one shut.
 * - v1.0 (2026-10-01): Initial creation.
 *
 * DEPENDENCIES:
 * - Stock accordion-custom.js must be defined before init (awaited via
 *   customElements.whenDefined) because its connectedCallback applies the
 *   open-by-default state this element then reconciles.
 */

class AccordionGroupModded extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    // Let the stock accordion-custom elements apply their default-open
    // state first; reconcile after they have upgraded.
    customElements.whenDefined('accordion-custom').then(() => {
      requestAnimationFrame(() => {
        if (this.isConnected) this.#init();
      });
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #init() {
    const { signal } = this.#controller;
    // Only rows belonging to THIS group — a nested accordion's rows
    // belong to their own (closer) accordion-group-modded.
    const detailsList = [...this.querySelectorAll('details')].filter(
      (details) => details.closest('accordion-group-modded') === this
    );

    if (this.hasAttribute('data-exclusive')) {
      // First default-open row wins before names enforce exclusivity.
      let firstOpen = null;
      for (const details of detailsList) {
        if (!details.open) continue;
        if (firstOpen) details.open = false;
        else firstOpen = details;
      }

      const groupName = `accordion-modded-${this.dataset.groupId || 'group'}`;
      for (const details of detailsList) details.name = groupName;
    }

    if (this.hasAttribute('data-scroll-on-open')) {
      for (const details of detailsList) {
        details.addEventListener(
          'toggle',
          () => {
            if (!details.open) return;
            requestAnimationFrame(() => {
              if (details.getBoundingClientRect().top < 0) {
                details.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            });
          },
          { signal }
        );
      }
    }

    const openFromHash = () => {
      const hash = window.location.hash.slice(1);
      if (!hash) return;

      const target = this.querySelector(`details[id='${CSS.escape(hash)}']`);
      if (!target || target.closest('accordion-group-modded') !== this) return;

      target.open = true;
      requestAnimationFrame(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    };

    openFromHash();
    window.addEventListener('hashchange', openFromHash, { signal });
  }
}

if (!customElements.get('accordion-group-modded')) {
  customElements.define('accordion-group-modded', AccordionGroupModded);
}
